/**
 * AppButton — drop-in replacement for @atlaskit/button
 * Matches atlaskit button appearance exactly.
 * Props: appearance, disabled, onClick, type, autoFocus, startIcon, children
 * appearance: 'primary' | 'default' | 'subtle' | 'warning' | 'danger'
 */
import * as React from 'react';
import styled from 'styled-components';

const base = `
  appearance: none !important;
  -webkit-appearance: none !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 4px !important;
  border: 1px solid transparent !important;
  border-radius: 3px !important;
  cursor: pointer !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  padding: 0 12px !important;
  height: 32px !important;
  min-height: 32px !important;
  white-space: nowrap !important;
  text-decoration: none !important;
  transition: background 0.1s ease, box-shadow 0.1s ease;
  outline: none;
  box-shadow: none !important;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryBtn = styled.button`
  ${base}
  background: #0C66E4 !important;
  color: #fff !important;
  &:hover:not(:disabled) { background: #0055CC; }
  &:active:not(:disabled) { background: #004EC2; }
`;

const DefaultBtn = styled.button`
  ${base}
  background: #FFFFFF !important;
  color: #172B4D !important;
  border: 1px solid #C1C7D0 !important;
  &:hover:not(:disabled) { background: #DFE1E6; }
  &:active:not(:disabled) { background: #C7D0D6; }
`;

const SubtleBtn = styled.button`
  ${base}
  background: #FFFFFF !important;
  color: #42526E !important;
  border: 1px solid #C1C7D0 !important;
  &:hover:not(:disabled) { background: #F1F2F4; }
  &:active:not(:disabled) { background: #DFE1E6; }
`;

const WarningBtn = styled.button`
  ${base}
  background: #FFAB00 !important;
  color: #172B4D !important;
  &:hover:not(:disabled) { background: #FF991F; }
  &:active:not(:disabled) { background: #FF8B00; }
`;

const DangerBtn = styled.button`
  ${base}
  background: #DE350B !important;
  color: #fff !important;
  &:hover:not(:disabled) { background: #BF2600; }
`;

const components = {
  primary: PrimaryBtn,
  default: DefaultBtn,
  subtle:  SubtleBtn,
  warning: WarningBtn,
  danger:  DangerBtn,
};

export const AppButton = ({ appearance = 'default', children, startIcon, isDisabled, disabled, ...props }) => {
  const Btn = components[appearance] || DefaultBtn;
  const buttonDisabled = Boolean(disabled || isDisabled);
  return (
    <Btn {...props} disabled={buttonDisabled}>
      {startIcon && <span style={{ display: 'inline-flex', alignItems: 'center' }}>{startIcon}</span>}
      {children}
    </Btn>
  );
};

export default AppButton;
